import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, KeyRound, AlertCircle, CheckCircle2, RefreshCw } from "lucide-react";
import { supabase } from "../../lib/supabase";

const OTP_COOLDOWN = 60;

const PasswordResetOtpVerificationScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location?.state?.email || "";

  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(OTP_COOLDOWN);
  const cooldownRef = useRef(null);
  const inputRefs = useRef([]);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) {
      navigate("/password-reset-email-screen", { replace: true });
    }
  }, [email, navigate]);

  // Start cooldown on mount
  useEffect(() => {
    startCooldown();
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  function startCooldown() {
    setCooldown(OTP_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  const handleDigitChange = (index, value) => {
    const digit = value?.replace(/\D/g, "")?.slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setOtpError("");
    // Auto-advance
    if (digit && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === "Backspace" && !otpDigits?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pasted = e?.clipboardData?.getData("text")?.replace(/\D/g, "")?.slice(0, 6);
    if (pasted) {
      const newDigits = [...otpDigits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted?.[i] || "";
      }
      setOtpDigits(newDigits);
      setOtpError("");
      const nextEmpty = pasted?.length < 6 ? pasted?.length : 5;
      inputRefs?.current?.[nextEmpty]?.focus();
    }
  };

  const otpCode = otpDigits?.join("");
  const isComplete = otpCode?.length === 6;

  const handleVerify = async () => {
    if (!isComplete) {
      setOtpError("Please enter the complete 6-digit code.");
      return;
    }
    setIsVerifying(true);
    setOtpError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-email-otp", {
        body: { email: email?.trim(), action: "verify", otp: otpCode },
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Invalid code. Please try again.");
      } else if (data?.verified) {
        navigate("/password-reset-new-password-screen", {
          state: { email: email?.trim(), verified: true },
        });
      }
    } catch (err) {
      setOtpError("Network error. Please check your connection and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setIsResending(true);
    setOtpError("");
    setOtpDigits(["", "", "", "", "", ""]);
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-email-otp", {
        body: { email: email?.trim(), action: "send" },
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Failed to resend code. Please try again.");
      } else {
        startCooldown();
        inputRefs?.current?.[0]?.focus();
      }
    } catch (err) {
      setOtpError("Network error. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="w-full px-6 py-4 flex items-center justify-between border-b border-border bg-white">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-white font-bold text-sm">LPS</span>
          </div>
          <span className="font-semibold text-foreground text-sm hidden sm:block">Volunteer Portal</span>
        </Link>
        <button
          onClick={() => navigate("/password-reset-email-screen")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-border p-8">
            {/* Icon + Heading */}
            <div className="flex flex-col items-center mb-8">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <KeyRound className="w-7 h-7 text-primary" />
              </div>
              <h1
                className="text-2xl font-bold text-foreground mb-1.5 text-center"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                Check your email
              </h1>
              <p className="text-sm text-muted-foreground text-center">
                We sent a 6-digit verification code to{" "}
                <span className="font-medium text-foreground">{email}</span>
              </p>
            </div>

            {/* Step indicator */}
            <div className="flex items-center gap-2 mb-6">
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </div>
                <span className="text-xs font-medium text-green-600">Email</span>
              </div>
              <div className="flex-1 h-px bg-primary" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center">2</div>
                <span className="text-xs font-medium text-primary">Verify</span>
              </div>
              <div className="flex-1 h-px bg-border" />
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground text-xs font-bold flex items-center justify-center">3</div>
                <span className="text-xs text-muted-foreground">New Password</span>
              </div>
            </div>

            {/* OTP Error */}
            {otpError && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 mb-5">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{otpError}</p>
              </div>
            )}

            {/* OTP Digit Inputs */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                  Verification Code
                </label>
                <div className="flex gap-2 justify-center" onPaste={handlePaste}>
                  {otpDigits?.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleDigitChange(index, e?.target?.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      className={`w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-bold rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-0 ${
                        otpError
                          ? "border-red-400 focus:ring-red-300 bg-red-50"
                          : digit
                          ? "border-primary bg-primary/5 focus:ring-primary/30" :"border-gray-200 focus:ring-primary/30 focus:border-primary bg-white"
                      }`}
                      style={{ height: "3.25rem" }}
                    />
                  ))}
                </div>
              </div>

              {/* Verify Button */}
              <button
                type="button"
                onClick={handleVerify}
                disabled={isVerifying || !isComplete}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg bg-primary text-white font-semibold text-sm transition-all hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Verifying…
                  </>
                ) : (
                  "Verify Code"
                )}
              </button>

              {/* Resend */}
              <div className="text-center">
                {cooldown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Resend code in{" "}
                    <span className="font-medium text-foreground tabular-nums">{cooldown}s</span>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={isResending}
                    className="inline-flex items-center gap-1.5 text-sm text-primary font-medium hover:text-primary/80 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isResending ? "animate-spin" : ""}`} />
                    {isResending ? "Sending…" : "Resend code"}
                  </button>
                )}
              </div>
            </div>

            {/* Security note */}
            <div className="mt-6 p-3 bg-muted/40 rounded-lg border border-border">
              <p className="text-xs text-muted-foreground text-center">
                🔒 Code expires in 10 minutes. Check your spam folder if you don't see it.
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Wrong email?{" "}
            <button
              onClick={() => navigate("/password-reset-email-screen")}
              className="text-primary font-medium hover:text-primary/80 transition-colors"
            >
              Go back
            </button>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PasswordResetOtpVerificationScreen;
